import { useQuery } from '@tanstack/react-query';
import { formService } from '../services/form.service';
import { FormDto, FormSummaryDto } from '../services/form.service';
import { Form } from '../types/forms';
import { formDtoToForm } from '../services/form.service';

export const FORMS_QUERY_KEY = 'forms';

/**
 * Fetch forms not linked to any program version item ("orphan" forms)
 * @param organizationId Organization ID
 * @returns FormSummaryDto array
 */
export const useOrphanForms = (organizationId: string) => {
  return useQuery({
    queryKey: [FORMS_QUERY_KEY, 'orphans', organizationId],
    queryFn: async () => {
      const response = await formService.getOrphans(organizationId);
      return (((response as any).data ?? []) as FormSummaryDto[]);
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Batch fetch multiple forms by their IDs
 * @param organizationId Organization ID
 * @param formIds Array of form UUIDs to fetch
 * @returns Map of form ID to Form object
 */
export const useFormsByIds = (organizationId: string, formIds: string[]) => {
  return useQuery({
    queryKey: [FORMS_QUERY_KEY, 'batch', organizationId, formIds],
    queryFn: async () => {
      const response = await formService.getByIds(organizationId, formIds);
      
      // The API returns { data: { formId: FormDto } } structure
      // The axios interceptor unwraps one level, so response should be { formId: FormDto }
      const formsDtoMap: Record<string, FormDto> = (response as any).data || response;
      
      // Convert FormDto to Form and return as a map
      const formsMap: Record<string, Form> = {};
      for (const [formId, formDto] of Object.entries(formsDtoMap)) {
        try {
          formsMap[formId] = formDtoToForm(formDto as FormDto);
        } catch (error) {
          console.error(`Error converting form ${formId}:`, error);
        }
      }
      
      return formsMap;
    },
    enabled: !!organizationId && formIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Fetch a single form by ID
 * @param organizationId Organization ID
 * @param formId Form UUID
 * @returns Form object or undefined
 */
export const useFormById = (organizationId: string, formId: string) => {
  return useQuery({
    queryKey: [FORMS_QUERY_KEY, organizationId, formId],
    queryFn: async () => {
      const response = await formService.getById(organizationId, formId);
      const dto = (response as any).data as FormDto | undefined;
      if (!dto) {
        throw new Error('Form not found');
      }
      return formDtoToForm(dto);
    },
    enabled: !!organizationId && !!formId,
    staleTime: 5 * 60 * 1000,
  });
};
